import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Building2, Loader2, Plus, RefreshCw, Save, Search } from 'lucide-react';

import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Switch } from '../components_dashboard/ui/switch';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const NEW_PROVIDER_ID = '__new__';

const EMPTY_PROVIDER_DRAFT = {
  id: '',
  name: '',
  concurrency: '',
  status: 'inactive',
  __isNew: true,
};

export default function ProvidersDashboard() {
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [draft, setDraft] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProviders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return providers;
    return providers.filter((entry) => String(entry.name || '').toLowerCase().includes(query));
  }, [providers, searchQuery]);

  const selectedProvider = useMemo(() => (
    providers.find((entry) => entry.id === selectedProviderId) || null
  ), [providers, selectedProviderId]);

  const isCreatingProvider = Boolean(draft?.__isNew);
  const isDraftActive = String(draft?.status || '').toLowerCase() === 'active';

  const fetchProviders = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingProviders(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/providers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(response.data?.providers) ? response.data.providers : [];
      setProviders(rows);
      setSelectedProviderId((current) => {
        if (current === NEW_PROVIDER_ID) return current;
        if (current && rows.some((entry) => entry.id === current)) return current;
        return rows[0]?.id || '';
      });
    } catch (error) {
      console.error('Failed to fetch providers', error);
      toast.error(error.response?.data?.detail || 'Failed to load providers.');
      setProviders([]);
      setSelectedProviderId('');
    } finally {
      setLoadingProviders(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    if (selectedProviderId === NEW_PROVIDER_ID) {
      setDraft({ ...EMPTY_PROVIDER_DRAFT });
    } else if (selectedProvider) {
      setDraft({ ...selectedProvider, __isNew: false });
    } else {
      setDraft(null);
    }
  }, [selectedProvider, selectedProviderId]);

  const handleCreateDraft = () => {
    setSelectedProviderId(NEW_PROVIDER_ID);
  };

  const handleFieldChange = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const applySavedProvider = (saved, isNewRecord) => {
    setDraft({ ...saved, __isNew: false });
    setSelectedProviderId(saved.id);
    setProviders((current) => (
      isNewRecord
        ? [saved, ...current]
        : current.map((entry) => (entry.id === saved.id ? saved : entry))
    ));
  };

  const handleSave = async () => {
    if (!draft) return;

    if (!String(draft.name || '').trim()) {
      toast.error('Name is required.');
      return;
    }
    if (draft.concurrency === '' || draft.concurrency === null || draft.concurrency === undefined) {
      toast.error('Concurrency is required.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: draft.name || null,
        concurrency: Number(draft.concurrency),
        status: draft.status || null,
      };

      const response = isCreatingProvider
        ? await axios.post(`${apiUrl}/api/v1/admin/providers`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        : await axios.patch(`${apiUrl}/api/v1/admin/providers/${selectedProviderId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

      const updatedProvider = response.data;
      applySavedProvider(updatedProvider, isCreatingProvider);
      toast.success(isCreatingProvider ? 'Provider created.' : 'Provider updated.');
    } catch (error) {
      console.error('Failed to save provider', error);
      toast.error(error.response?.data?.detail || 'Failed to save provider.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <main className="flex-1 flex flex-col min-w-0 p-6 lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                <Building2 className="w-3.5 h-3.5" />
                Provider Controls
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Provider Manager</h1>
              <p className="mt-1 text-slate-500">Manage providers in the `providers` collection and their concurrency limits.</p>
            </div>

            <Button
              variant="outline"
              onClick={fetchProviders}
              disabled={loadingProviders}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              {loadingProviders ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh Providers
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100">
                <div className="space-y-3">
                  <Button
                    onClick={handleCreateDraft}
                    className="w-full text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Provider
                  </Button>
                  <div className="relative">
                    <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search providers..."
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <div className="max-h-[72vh] overflow-y-auto">
                {loadingProviders ? (
                  <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading providers...
                  </div>
                ) : filteredProviders.length === 0 ? (
                  <div className="px-4 py-10 text-sm text-center text-slate-500">
                    No providers match the current search.
                  </div>
                ) : (
                  filteredProviders.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => setSelectedProviderId(entry.id)}
                      className={`flex w-full items-start gap-3 border-b border-slate-100 px-4 py-4 text-left transition-colors last:border-b-0 ${
                        selectedProviderId === entry.id ? 'bg-violet-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-violet-100 text-violet-700">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate text-slate-900">{entry.name || 'Unnamed provider'}</div>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                            Concurrency: {entry.concurrency ?? 0}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 font-medium ${
                            String(entry.status || '').toLowerCase() === 'active'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-red-50 text-red-700'
                          }`}
                          >
                            {String(entry.status || '').toLowerCase() === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              {loadingProviders ? (
                <div className="flex min-h-[520px] items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading provider details...
                </div>
              ) : !draft ? (
                <div className="flex min-h-[520px] items-center justify-center gap-2 text-sm text-slate-500">
                  Select a provider to inspect.
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  <div className="flex flex-col gap-4 pb-6 border-b border-slate-100 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">{draft.name || 'Create Provider'}</h2>
                      <p className="mt-1 text-sm text-slate-500">
                        {isCreatingProvider ? 'Fill the provider details and save to create a new record.' : `ID: ${draft.id}`}
                      </p>
                    </div>

                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      {isCreatingProvider ? 'Create Provider' : 'Save Changes'}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={draft.name || ''} onChange={(event) => handleFieldChange('name', event.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Concurrency</Label>
                      <Input
                        type="number"
                        min="0"
                        value={draft.concurrency ?? ''}
                        onChange={(event) => handleFieldChange('concurrency', event.target.value)}
                      />
                      <p className="text-xs text-slate-500">Max concurrent calls allowed for this provider.</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Activation Status</h3>
                      <p className="text-sm text-slate-500">Toggle this to set the provider as active or inactive.</p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                      <div className="p-4 border rounded-xl border-slate-200">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-medium text-slate-900">{isDraftActive ? 'Active' : 'Inactive'}</div>
                            <p className="mt-1 text-sm text-slate-500">
                              {isDraftActive
                                ? 'This provider is currently available for selection.'
                                : 'This provider is currently disabled.'}
                            </p>
                          </div>
                          <Switch
                            checked={isDraftActive}
                            onCheckedChange={(checked) => handleFieldChange('status', checked ? 'active' : 'inactive')}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
